'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import type { BlogPost } from '@/lib/blog/types'
import { BLOG_CONSTANTS } from '@/lib/blog/config'
import { trackEvent } from '@/lib/blog/analytics'
import { ArticleCard } from './article-card'
import { TagFilter } from './tag-filter'

interface BlogListingLayoutProps {
  posts: BlogPost[]
  tags: string[]
  selectedTags?: string[]
}

export function BlogListingLayout({
  posts,
  tags,
  selectedTags = [],
}: BlogListingLayoutProps) {
  const router = useRouter()
  const [visibleCount, setVisibleCount] = useState(BLOG_CONSTANTS.POSTS_PER_PAGE)

  const visiblePosts = posts.slice(0, visibleCount)
  const hasMore = visibleCount < posts.length

  function handleTagsChange(newTags: string[]) {
    setVisibleCount(BLOG_CONSTANTS.POSTS_PER_PAGE)
    trackEvent('tag_click', { tags: newTags })

    if (newTags.length === 0) {
      router.push('/blog')
    } else {
      router.push(`/blog/tag/${newTags.join('/')}`)
    }
  }

  function handleTagClick(tag: string) {
    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag]
    handleTagsChange(newTags)
  }

  function handleShowMore() {
    setVisibleCount((prev) => prev + BLOG_CONSTANTS.POSTS_PER_SHOW_MORE)
    trackEvent('show_more_click', {})
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[var(--color-text-primary)]">
          Blog
        </h1>
        <p className="mt-2 text-[var(--color-text-secondary)]">
          Insights, tutorials, and best practices for AI-powered workflows.
        </p>
      </div>

      {/* Main layout */}
      <div className="flex flex-col gap-8 lg:flex-row">
        {/* Grid */}
        <div className="min-w-0 flex-1">
          {visiblePosts.length === 0 ? (
            <div className="py-16 text-center text-[var(--color-text-muted)]">
              No articles found matching your filters.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                {visiblePosts.map((post) => (
                  <ArticleCard
                    key={post.slug}
                    post={post}
                    onTagClick={handleTagClick}
                  />
                ))}
              </div>

              {hasMore && (
                <div className="mt-8 text-center">
                  <button
                    type="button"
                    onClick={handleShowMore}
                    className="rounded-lg bg-[var(--color-brand-indigo)] px-6 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
                  >
                    Show More
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar */}
        <aside className="w-full flex-shrink-0 lg:w-64">
          <TagFilter
            tags={tags}
            selectedTags={selectedTags}
            onTagsChange={handleTagsChange}
            postCount={posts.length}
          />
        </aside>
      </div>
    </div>
  )
}
